import merge from 'lodash-es/merge';
import type { DateTime } from 'luxon';
import axios from 'axios';

/**
 * BannersService
 */
export default class BannersService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     * @param except number[]
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager, except: number[] = []): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`admin/wk/banners`, {
            params: merge({}, filter, pager.data(), { except: except })
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/wk/banners/${id}`)).data;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post(`admin/wk/banners`, model)).data as Resource<FormModel>;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.put(`admin/wk/banners/${id}`, model)).data as Resource<FormModel>;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/wk/banners/${id}`)).data as Statement;
    }

    /**
     * @param id number
     *
     * @returns Promise<Pagination<EntityVersion>>
     */
    public static async getVersions(id: number, pager: Pager): Promise<Pagination<EntityVersion>>
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/wk/banners/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    /**
     * @param id number
     * @param status PublicationEnum
     *
     * @returns Promise<Statement>
     */
    public static async status(id: number, status: PublicationEnum): Promise<Statement>
    {
        return (await axios.patch(`admin/wk/banners/${id}/status`, {
            status: status
        })).data;
    }

    /**
     * @param id number
     * @param model PublishModel
     *
     * @returns Promise<Statement>
     */
    public static async publish(id: number, model: PublishModel): Promise<Statement>
    {
        return (await axios.patch(`admin/wk/banners/${id}/publish`, model)).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<WidgetItem>
    */
    public static async fetchAutocompleteBannerModel(id: number): Promise<WidgetItem>
    {
        return (await axios.get<WidgetItem>(`admin/wk/banners/autocomplete/${id}`)).data;
    }

    /**
     * @param search string
     * @param limit number
     * @param width number
     * @param height number
     *
     * @returns Promise<WidgetItem[]>
     */
    public static async getAutocompleteBannerPlaceholdersList(search: string, width: number, height: number, limit: number = 10): Promise<WidgetItem[]>
    {
        return (await axios.get<WidgetItem[]>(`admin/wk/banners/placeholders/autocomplete`, {
            params: {
                search: search,
                width: width,
                height: height,
                limit: limit
            }
        })).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<WidgetItem[]>
     */
    public static async getAutocompleteBannerList(search: string, limit: number = 10): Promise<WidgetItem[]>
    {
        return (await axios.get<WidgetItem[]>(`admin/wk/banners/autocomplete`, {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<WidgetItem[]>
     */
    public static async getAutocompleteList(search: string, limit: number): Promise<WidgetItem[]>
    {
        return (await axios.get<WidgetItem[]>(`admin/wk/banners/widgets/autocomplete`, {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<WidgetItem>
    */
    public static async fetchAutocompleteModel(id: number): Promise<WidgetItem>
    {
        return (await axios.get<WidgetItem>(`admin/wk/banners/widgets/autocomplete/${id}`)).data;
    }

    /**
     * @param articleId number
     *
     * @returns Promise<DetailsModel[]>
     */
    public static async getListByArticleId(articleId: number): Promise<DetailsModel[]>
    {
        return (await axios.get<DetailsModel[]>(`admin/wk/banners/by-article/${articleId}`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<DetailsModel>
     */
    public static async fetchDetails(id: number): Promise<DetailsModel>
    {
        return (await axios.get<DetailsModel>(`admin/wk/banners/${id}/details`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<DetailsModel>
     */
    public static async fetchDetailsByOrigin(id: number): Promise<DetailsModel>
    {
        return (await axios.get<DetailsModel>(`admin/wk/banners/${id}/detailsByOrigin`)).data;
    }
}

export interface WidgetItem
{
    id: number;
    name: string;
    image?: File;
}

export interface ListItemModel
{
    id: number;
    name: string;
    image?: File;
    createdBy: string;
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
    ownerId: number;
    resourceId: number;
    originId: number;
    clickCount: number;
    widgetName: string;
    isHiddenForEditorial: boolean;
    width: number;
    height: number;
}

export interface DetailsModel
{
    id: number;
    name: string;
    link: string;
    image?: File;
    isPublished: boolean;
    isHiddenForEditorial: boolean;
    isOpenInNewTab: boolean;
    width: number;
    height: number;
    widgetId: number;
    placeholderId: number;
    sectionsIds: number[];
    clickCount: number;
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
    originId: number;
}

export interface UpsertModel
{
    id: number;
    name: string;
    link: string;
    image?: File;
    isPublished: boolean;
    isHiddenForEditorial: boolean;
    isOpenInNewTab: boolean;
    width: number;
    height: number;
    widgetId: number;
    placeholderId: number;
    sectionsIds: number[];
    clickCount: number;
    description: string;
    isForNewsletter: boolean;
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
}

export interface FilterModel
{
    status: string;
    name: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    isHiddenForEditorial: boolean;
    sectionId: number;
    widgetId: number;
    clickCount: number;
    isPublished: boolean;
    isForNewsletter: boolean;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}

export interface AutocompleteFilterModel
{
    search: string;
    height: number;
    width: number;
    limit: number;
}
